import { tmsAllowRoles } from 'data/facts/userRolesValidations';
import { useStore } from 'hooks/useStore';
import {
  CotTypesInterface,
  targetMarketShareInstanceInterface,
} from 'Interfaces/FactsInterfaces/drugInterfaces';
import { Nullable } from 'primereact/ts-helpers';
import { SetStateAction, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { createEmptyDataForIndireTargetMarketShare } from 'screens/FactsScreen/components/ndcStrategyTable/utils/utilities';
import { factScreenServices } from 'services/factsScreenServices';
import {
  getFirstDayOfMonth,
  getLastDayOfMonth,
  getNextMonthFirstDay,
  getOneBiggerDate,
  getOneSmallerDate,
  getTomorowsDate,
  DateConversionUtility,
} from 'utils/dateUtilities';
import { getRoundedValueForMarketShare } from 'utils/computation';
import { deepCopy } from 'utils/deepCopy';
import { checkIfFunctionalityEnabled } from 'utils/facts/roleBasedFunctionalityAccess';
import {
  checkFutureStrategiesOverlap,
  createDateRangesForOppositeStrategyAndCheckValidity,
} from './utils';

const findStrategyOptionMatching = (allOptions: { name: string; code: string }[], code: string) => {
  let temp = { code: '', name: '' };
  allOptions.forEach(option => {
    if (option.name === code) {
      temp = option;
    }
  });
  return temp;
};

function isNumeric(value: string) {
  return /^-?\d+$/.test(value);
}

export const useMarketStrategyEditModal = ({
  toggleVisibility,
  isEditPopUp,
}: {
  toggleVisibility: () => void;
  isEditPopUp: boolean;
}) => {
  const [date, setDate] = useState<Nullable<Date>>(null);
  const {
    UserStore: { userModulesRoles },
    FactsStore: {
      currentTabIndex,
      isMarketStrategyTableDataLoading,
      marketStrategyHistoricalData,
      oppositeStrategyMarketStrategyHistoricalData,
      drugConceptsList,
      isMarketHistoryLoading,
      selectedCOTMarketStrategyForEdit,
      isDirectStrategyEditOpen,
      selectedMarketStrategyIndexForEdit,
      marketStrategyTableData,
      directMarketStrategyTableData,
      indirectMarketStrategyTableData,
      isMarketHisotryVisible,
      toggleMarketStrategyAddModalVisibility,
      marketStrategyAddModalVisibility,
      toggleMarketHitoryVisibility,
      targetMarketStrategyCodeOptions,
      setMarketHistoricalFutureDataForCot,
      tableDataForEditModal,
      handleFetchTargetMarketShareForSelectedGFC,
    },
  } = useStore();

  const getRowDataByType = (
    type: string,
    isDirectTable: boolean
  ): targetMarketShareInstanceInterface | null => {
    let rowData: targetMarketShareInstanceInterface | null = null;
    if (isDirectTable) {
      directMarketStrategyTableData().forEach(row => {
        if (row.COT === type) {
          rowData = row;
        }
      });
    } else {
      indirectMarketStrategyTableData().forEach(row => {
        if (row.COT === type) {
          rowData = row;
        }
      });
    }
    return rowData;
  };

  const [typeAlreadyExists, _setTypeAlreadyExists] = useState(
    getRowDataByType(
      selectedCOTMarketStrategyForEdit as CotTypesInterface,
      isDirectStrategyEditOpen
    )
      ? true
      : false
  );
  const [indtialMarketShareData, setIndtialMarketShareData] = useState(
    isEditPopUp
      ? !typeAlreadyExists
        ? createEmptyDataForIndireTargetMarketShare()
        : {
            ...deepCopy(tableDataForEditModal()),
            TargetMarketShare: (
              getRoundedValueForMarketShare(deepCopy(tableDataForEditModal())?.TargetMarketShare) *
              100
            ).toString(),
            StartDate: DateConversionUtility.convertLocalToDBDateString(
              getFirstDayOfMonth(deepCopy(tableDataForEditModal())?.StartDate)
            ),
            EndDate: DateConversionUtility.convertLocalToDBDateString(
              getLastDayOfMonth(deepCopy(tableDataForEditModal())?.EndDate)
            ),
          }
      : tableDataForEditModal()
        ? createEmptyDataForIndireTargetMarketShare(tableDataForEditModal().MarketShareID)
        : createEmptyDataForIndireTargetMarketShare()
  );
  const [marketShareData, setMarketSharedata] = useState(
    isEditPopUp
      ? !typeAlreadyExists
        ? createEmptyDataForIndireTargetMarketShare()
        : {
            ...deepCopy(tableDataForEditModal()),
            TargetMarketShare: (
              getRoundedValueForMarketShare(deepCopy(tableDataForEditModal())?.TargetMarketShare) *
              100
            ).toString(),
            StartDate: DateConversionUtility.convertLocalToDBDateString(
              getFirstDayOfMonth(deepCopy(tableDataForEditModal())?.StartDate)
            ),
            EndDate: DateConversionUtility.convertLocalToDBDateString(
              getLastDayOfMonth(deepCopy(tableDataForEditModal())?.EndDate)
            ),
          }
      : tableDataForEditModal()
        ? createEmptyDataForIndireTargetMarketShare(tableDataForEditModal().MarketShareID)
        : createEmptyDataForIndireTargetMarketShare()
  );
  const [useCurrent, setUseCurrent] = useState(typeAlreadyExists ? true : false);

  const [allStrategyOptions, setAllStrategyOptions] = useState(targetMarketStrategyCodeOptions());
  const [selectedStrategy, setSelectedStrategy] = useState<{
    code: string;
    name: string;
  }>({ code: '', name: '' });

  const [errorDetails, setErrorDetails] = useState({
    isError: false,
    errorDescription: '',
  });
  const [isUpdateLoading, setIsUpdateLoading] = useState(false);
  const [isHistoryVisible, setIsHistoryVisible] = useState(false);

  const [isMarketHistoryNewAddVisible, setIsNewMarketHistoryVisible] = useState(true);
  const [newlyAddedFutureTmsData, setNewlyAddedFutureTmsData] = useState<{
    COT: string;
    value: string;
    startDate: string;
    endDate: string;
  } | null>(null);

  const [tmsExtendPopUpVisibility, setTmsExtendPopUpVisibility] = useState(true);
  const [tmsExtendPopUpData, setTmsExtendPopUpData] = useState<{
    COT: string;
    value: string;
    startDate: string;
    endDate: string;
  } | null>(null);

  const [isUpdateTriggered, setIsUpdateTriggered] = useState(false);

  const findStrategyId = () => {
    let id = '';
    targetMarketStrategyCodeOptions().forEach(option => {
      if (option.name === selectedStrategy.name) {
        id = option.code;
      }
    });
    return id;
  };

  const resetError = () => {
    setErrorDetails({
      isError: false,
      errorDescription: '',
    });
  };

  const formUpdateMethods = {
    handleUpdateTms: (tms: string) => {
      resetError();
      if (tms != '' && !isNumeric(tms)) {
        setMarketSharedata({
          ...marketShareData,
          TargetMarketShare: marketShareData.TargetMarketShare,
        });
        return;
      }
      if (tms.indexOf('.') !== -1) {
        setMarketSharedata({
          ...marketShareData,
          TargetMarketShare: tms.split('.')[0],
        });
        return;
      }
      if ((parseFloat(tms) >= 0 && parseFloat(tms) <= 100) || tms === '') {
        setMarketSharedata({
          ...marketShareData,
          TargetMarketShare: tms.toString(),
        });
      }
    },

    handleStrategy: (strategyCode: string) => {
      resetError();
      const tempState = deepCopy(marketShareData);
      tempState.TMSStrategyID.TMSStrategyCode = strategyCode;
      setMarketSharedata(tempState);
    },

    handleUpdateUseCurent: () => {
      resetError();
      if (!useCurrent) {
        setSelectedStrategy(
          findStrategyOptionMatching(
            allStrategyOptions,
            indtialMarketShareData.TMSStrategyID.TMSStrategyCode
          )
        );
      }
      setUseCurrent(!useCurrent);
    },

    handleUpdateStartDate: (updatedStartDate: Date) => {
      resetError();
      setMarketSharedata({
        ...deepCopy(marketShareData),
        StartDate: DateConversionUtility.convertLocalToDBDateString(
          getFirstDayOfMonth(updatedStartDate.toDateString())
        ),
      });
    },

    handleUpdateEnddate: (updatedEndDate: Date) => {
      resetError();
      setMarketSharedata({
        ...deepCopy(marketShareData),
        EndDate: DateConversionUtility.convertLocalToDBDateString(
          getLastDayOfMonth(updatedEndDate.toDateString())
        ),
      });
    },

    handleUpdateButtonClickForAdd: async () => {
      resetError();
      if (marketShareData.TargetMarketShare === '') {
        setErrorDetails({
          isError: true,
          errorDescription: 'Target Market share cannot be left blank',
        });
        return;
      }
      if (
        !typeAlreadyExists &&
        (marketShareData.TargetMarketShare === '' ||
          !marketShareData.StartDate ||
          !marketShareData.EndDate)
      ) {
        if (marketShareData.TargetMarketShare === '') {
          setErrorDetails({
            isError: true,
            errorDescription: 'Target Market share cannot be left blank',
          });
          return;
        } else if (!selectedStrategy.code) {
          setErrorDetails({
            isError: true,
            errorDescription: 'Please select a strategy',
          });
          return;
        } else if (!marketShareData.StartDate || !marketShareData.EndDate) {
          setErrorDetails({
            isError: true,
            errorDescription: 'Start date or End date cannot be left empty',
          });
          return;
        }
      }

      if (
        marketShareData.StartDate &&
        marketShareData.EndDate &&
        new Date(marketShareData.StartDate) >= new Date(marketShareData.EndDate)
      ) {
        setErrorDetails({
          isError: true,
          errorDescription: 'Start date should be lesser than end date',
        });
        return;
      }

      if (
        typeAlreadyExists &&
        indtialMarketShareData.StartDate === marketShareData.StartDate &&
        indtialMarketShareData.EndDate === marketShareData.EndDate
      ) {
        setErrorDetails({
          isError: true,
          errorDescription: 'Start date or End date needs to be different from initial dates',
        });
        return;
      }

      if (!selectedStrategy.code) {
        setErrorDetails({
          isError: true,
          errorDescription: 'Please select strategy before updating',
        });
        return;
      }

      // check the validity from the date ranges for the opposite strategy and compare tms sum validation, i.e it cannot be greater than 100 (in percent) or 1
      if (
        !createDateRangesForOppositeStrategyAndCheckValidity(
          parseFloat(marketShareData.TargetMarketShare) / 100,
          marketShareData.StartDate,
          marketShareData.EndDate,
          selectedCOTMarketStrategyForEdit as string,
          isDirectStrategyEditOpen ? 'Direct' : 'Indirect',
          marketStrategyTableData,
          oppositeStrategyMarketStrategyHistoricalData,
          setErrorDetails
        )
      ) {
        return;
      }

      // Check current date range does not overlaps with future activate strategy date ranges
      if (
        checkFutureStrategiesOverlap(
          marketShareData.StartDate,
          marketShareData.EndDate,
          setErrorDetails,
          marketStrategyHistoricalData
        )
      ) {
        return;
      }

      {
        setErrorDetails({
          isError: false,
          errorDescription: '',
        });
        try {
          setIsUpdateLoading(true);
          const res = await factScreenServices.updateMarketShareData({
            MarketShareID: marketShareData.MarketShareID as number,
            GFC: drugConceptsList[currentTabIndex].gfc,
            TargetMarketShare: (parseInt(marketShareData.TargetMarketShare) / 100).toString(),
            COT: selectedCOTMarketStrategyForEdit as string,
            TMSStrategyID: parseInt(findStrategyId()) as number,
            StartDate: marketShareData.StartDate,
            EndDate: marketShareData.EndDate,
            SellingCategory: isDirectStrategyEditOpen ? 'Direct' : 'Indirect',
          });

          setNewlyAddedFutureTmsData({
            COT: selectedCOTMarketStrategyForEdit as string,
            value: parseInt(marketShareData.TargetMarketShare).toString(),
            startDate: marketShareData.StartDate,
            endDate: marketShareData.EndDate,
          });
          setIsUpdateTriggered(true);
          setIsNewMarketHistoryVisible(true);
          setMarketHistoricalFutureDataForCot(res.future_tms);
        } catch (err: unknown) {
          const msg =
            err && typeof err === 'object' && 'response' in err
              ? (err as { response?: { data?: { error?: string } }; message?: string }).response?.data?.error ??
                (err as { message?: string }).message
              : (err as Error)?.message;
          toast.error(msg ?? 'An error occurred');
        } finally {
          setIsUpdateLoading(false);
        }
      }
    },
    handleUpdateButtonClickForEdit: async () => {
      resetError();
      if (
        !typeAlreadyExists &&
        (marketShareData.TargetMarketShare === '' ||
          !marketShareData.StartDate ||
          !marketShareData.EndDate)
      ) {
        if (marketShareData.TargetMarketShare === '') {
          setErrorDetails({
            isError: true,
            errorDescription: 'Target Market share cannot be left blank',
          });
          return;
        } else if (!selectedStrategy.code) {
          setErrorDetails({
            isError: true,
            errorDescription: 'Please select a strategy',
          });
          return;
        } else if (!marketShareData.StartDate || !marketShareData.EndDate) {
          setErrorDetails({
            isError: true,
            errorDescription: 'Start date or End date cannot be left empty',
          });
          return;
        }
      }

      if (
        marketShareData.StartDate &&
        marketShareData.EndDate &&
        new Date(marketShareData.StartDate) >= new Date(marketShareData.EndDate)
      ) {
        setErrorDetails({
          isError: true,
          errorDescription: 'Start date should be lesser than end date',
        });
        return;
      }

      if (typeAlreadyExists && indtialMarketShareData.EndDate === marketShareData.EndDate) {
        setErrorDetails({
          isError: true,
          errorDescription: 'End date needs to be different from initial dates',
        });
        return;
      }

      // check the validity from the date ranges for the opposite strategy and compare tms sum validation, i.e it cannot be greater than 100 (in percent) or 1
      if (
        !createDateRangesForOppositeStrategyAndCheckValidity(
          parseFloat(marketShareData.TargetMarketShare) / 100,
          marketShareData.StartDate,
          marketShareData.EndDate,
          selectedCOTMarketStrategyForEdit as string,
          isDirectStrategyEditOpen ? 'Direct' : 'Indirect',
          marketStrategyTableData,
          oppositeStrategyMarketStrategyHistoricalData,
          setErrorDetails
        )
      ) {
        return;
      }

      // Check for overlapping dates
      if (
        checkFutureStrategiesOverlap(
          marketShareData.StartDate,
          marketShareData.EndDate,
          setErrorDetails,
          marketStrategyHistoricalData
        )
      ) {
        return;
      }

      {
        // return
        setErrorDetails({
          isError: false,
          errorDescription: '',
        });
        try {
          setIsUpdateLoading(true);
          const res = await factScreenServices.extendMarketShareEndDate({
            MarketShareID: marketShareData.MarketShareID as number,
            GFC: drugConceptsList[currentTabIndex].gfc,
            TargetMarketShare: (parseInt(marketShareData.TargetMarketShare) / 100).toString(),
            COT: selectedCOTMarketStrategyForEdit as string,
            TMSStrategyID: parseInt(findStrategyId()) as number,
            StartDate: marketShareData.StartDate,
            EndDate: marketShareData.EndDate,
            SellingCategory: isDirectStrategyEditOpen ? 'Direct' : 'Indirect',
          });
          setIsUpdateTriggered(true);
          setTmsExtendPopUpData({
            COT: selectedCOTMarketStrategyForEdit as string,
            value: parseInt(marketShareData.TargetMarketShare) as unknown as string,
            startDate: marketShareData.StartDate,
            endDate: marketShareData.EndDate,
          });
          setTmsExtendPopUpVisibility(true);
          setMarketHistoricalFutureDataForCot(res.future_tms);
          // toast.success("TMS successfully updated");
        } catch (err: unknown) {
          const msg =
            err && typeof err === 'object' && 'response' in err
              ? (err as { response?: { data?: { error?: string } }; message?: string }).response?.data?.error ??
                (err as { message?: string }).message
              : (err as Error)?.message;
          toast.error(msg ?? 'An error occurred');
        } finally {
          setIsUpdateLoading(false);
        }
      }
    },
  };

  const modalHeader = () => {
    return (
      drugConceptsList[currentTabIndex].concept +
      ' - ' +
      selectedCOTMarketStrategyForEdit +
      ' ' +
      (isDirectStrategyEditOpen ? ' ( Direct Strategy )' : '( Indirect Strategy )')
    );
  };

  const maxDateFromGivenAndTodays = (givenDate: Date) => {
    const date = new Date();
    if (givenDate > date) {
      return givenDate;
    } else {
      return date;
    }
  };

  const handleClose = async () => {
    if (isUpdateTriggered) handleFetchTargetMarketShareForSelectedGFC();
    toggleVisibility();
  };

  const handleNewStrategySelect = (newOption: { code: string; name: string }) => {
    const tempAllOptions = targetMarketStrategyCodeOptions();
    setAllStrategyOptions(tempAllOptions);
    setSelectedStrategy(findStrategyOptionMatching(tempAllOptions, newOption.code));
  };

  const toggleNewMarketPopVisibility = () => {
    setIsNewMarketHistoryVisible(!setIsNewMarketHistoryVisible);
    setNewlyAddedFutureTmsData(null);
  };

  const toggleTmsExtendPopUpVisibility = () => {
    setTmsExtendPopUpVisibility(!tmsExtendPopUpVisibility);
    setTmsExtendPopUpData(null);
    handleClose();
  };

  const inputOperations = {
    tmsInput: {
      isDisabled: () => {
        return isEditPopUp ? true : false;
      },
      value: () => {
        return marketShareData?.TargetMarketShare;
      },
      onChange: (value: string) => {
        formUpdateMethods.handleUpdateTms(value);
      },
    },
    tmsStrategyInput: {
      isDisabled: () => {
        return isEditPopUp ? true : false;
      },
      value: () => {
        return selectedStrategy;
      },
      onChange: (
        value: SetStateAction<{
          code: string;
          name: string;
        }>
      ) => {
        setSelectedStrategy(value);
        resetError();
      },
      options: () => allStrategyOptions,
    },
    startDateInput: {
      isDisabled: () => {
        return isEditPopUp ? true : false;
      },
      minDate: () => {
        return getNextMonthFirstDay(new Date().toDateString());
      },
      maxDate: () => {
        return marketShareData.EndDate === indtialMarketShareData.EndDate
          ? undefined
          : getOneSmallerDate(DateConversionUtility.convertDBDateToLocal(marketShareData.EndDate)!);
      },
      value: () => {
        return DateConversionUtility.convertDBDateToLocal(marketShareData.StartDate);
      },
      onChange: (value: Nullable<Date>) => {
        formUpdateMethods.handleUpdateStartDate(value as Date);
      },
    },
    endDateInput: {
      isDisabled: () => {
        return false;
      },
      minDate: () => {
        return isEditPopUp
          ? getNextMonthFirstDay(new Date().toDateString())
          : !marketShareData.StartDate
            ? getTomorowsDate()
            : getOneBiggerDate(
                maxDateFromGivenAndTodays(
                  DateConversionUtility.convertDBDateToLocal(marketShareData.StartDate)!
                )
              );
      },
      value: () => {
        return DateConversionUtility.convertDBDateToLocal(marketShareData.EndDate);
      },
      onChange: (value: Nullable<Date>) => {
        formUpdateMethods.handleUpdateEnddate(value as Date);
      },
    },
  };

  useEffect(() => {
    resetError();
  }, [marketStrategyHistoricalData]);

  useEffect(() => {
    if (isEditPopUp) {
      if (typeAlreadyExists) {
        setMarketSharedata({
          ...deepCopy(tableDataForEditModal()),
          TargetMarketShare: (
            parseFloat(deepCopy(tableDataForEditModal())?.TargetMarketShare) * 100
          ).toString(),
        });
        setIndtialMarketShareData({
          ...deepCopy(tableDataForEditModal()),
          TargetMarketShare: (
            parseFloat(deepCopy(tableDataForEditModal())?.TargetMarketShare) * 100
          ).toString(),
        });
      } else {
        setMarketSharedata(createEmptyDataForIndireTargetMarketShare());
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- sync when selected index changes
  }, [selectedMarketStrategyIndexForEdit]);

  const tableDataForEdit = tableDataForEditModal();
  useEffect(() => {
    if (isEditPopUp) {
      if (typeAlreadyExists && tableDataForEdit) {
        setMarketSharedata({
          ...deepCopy(tableDataForEdit),
          TargetMarketShare: (
            parseFloat(deepCopy(tableDataForEdit)?.TargetMarketShare) * 100
          ).toString(),
        });
        setIndtialMarketShareData({
          ...deepCopy(tableDataForEdit),
          TargetMarketShare: (
            parseFloat(deepCopy(tableDataForEdit)?.TargetMarketShare) * 100
          ).toString(),
        });
      } else {
        setMarketSharedata(createEmptyDataForIndireTargetMarketShare());
      }
      setSelectedStrategy(
        typeAlreadyExists && tableDataForEdit
          ? findStrategyOptionMatching(
              allStrategyOptions,
              tableDataForEdit.TMSStrategyID.TMSStrategyCode
            )
          : { code: '', name: '' }
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- sync when edit popup or table data changes
  }, [isEditPopUp, tableDataForEdit]);

  return {
    typeAlreadyExists,
    setErrorDetails,
    isMarketStrategyTableDataLoading,
    selectedMarketStrategyIndexForEdit,
    selectedCOTMarketStrategyForEdit,
    indtialMarketShareData,
    isHistoryVisible,
    maxDateFromGivenAndTodays,
    setIsHistoryVisible,
    errorDetails,
    selectedStrategy,
    allStrategyOptions,
    setSelectedStrategy,
    isMarketHistoryLoading,
    useCurrent,
    isUpdateLoading,
    formUpdateMethods,
    modalHeader,
    marketShareData,
    drugConceptsList,
    isDirectStrategyEditOpen,
    targetMarketStrategyCodeOptions,
    marketStrategyTableData,
    directMarketStrategyTableData,
    indirectMarketStrategyTableData,
    currentTabIndex,
    date,
    setDate,
    isMarketHisotryVisible,
    toggleMarketHitoryVisibility,
    marketStrategyAddModalVisibility,
    toggleMarketStrategyAddModalVisibility,
    handleNewStrategySelect,
    isMarketHistoryNewAddVisible,
    toggleNewMarketPopVisibility,
    newlyAddedFutureTmsData,
    resetError,
    checkIfFunctionalityEnabled,
    userModulesRoles,
    tmsAllowRoles,
    handleClose,
    inputOperations,
    tmsExtendPopUpVisibility,
    tmsExtendPopUpData,
    toggleTmsExtendPopUpVisibility,
  };
};
